import type { Enums, Tables } from '../../types/database.types';
import type { Profile } from '../auth/access';

export type Project = Tables<'projects'>;
export type ProjectMember = Tables<'project_members'>;
export type ProjectTemplate = Tables<'project_templates'>;
export type ProjectSection = Tables<'sections'>;
export type ProjectStatus = Tables<'project_statuses'>;

export type ProjectVisibility = Enums<'project_visibility'>;
export type ProjectRole = Enums<'project_role'>;

/**
 * The project-level workflow label (distinct from ProjectStatus above,
 * which is the per-project set of TASK statuses).
 */
export type ProjectWorkflowStatus = Enums<'project_status'>;

export const PROJECT_WORKFLOW_LABELS: Record<ProjectWorkflowStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In Progress',
  // Key order is chip order — buildProjectColumns walks these keys. Review
  // before revision because that is the order the work happens in: something
  // is looked at, and only then does it come back needing changes.
  for_review: 'For Review',
  // Same words as the task column of the same name (20261026000100). Two
  // levels of this product describe the same state, and describing it twice
  // differently is how people stop trusting either.
  for_revision: 'For Revision',
  on_hold: 'On Hold',
  completed: 'Completed',
};

export const PROJECT_WORKFLOW_TONES: Record<
  ProjectWorkflowStatus,
  // 'revision' and 'review' join the union so the project-level badge is the
  // same colour as the task column it mirrors. Both were already valid
  // BadgeTones; this local type was simply narrower than the design system.
  //
  // The two tones being distinct matters more here than usual: "For Review"
  // and "For Revision" differ by one letter and sit next to each other, so
  // colour is doing real work telling them apart at a glance.
  'neutral' | 'brand' | 'warning' | 'success' | 'review' | 'revision'
> = {
  not_started: 'neutral',
  in_progress: 'brand',
  for_review: 'review',
  for_revision: 'revision',
  on_hold: 'warning',
  completed: 'success',
};

/** A project list row: identity fields only, no counts and no analytics. */
export type ProjectListItem = Pick<
  Project,
  | 'id'
  | 'name'
  | 'description'
  | 'visibility'
  | 'owner_id'
  | 'status'
  | 'category'
  | 'due_date'
  | 'updated_at'
>;

/** An active TAP person who can be added to a project. */
export interface TapPerson {
  id: string;
  display_name: string;
  email: string;
  /** Same column the member row and the Inbox actor carry; null means initials. */
  avatar_url: string | null;
}

/**
 * The roles a manager may grant from the members list. Ownership is not one of
 * them: it moves through the Project owner picker on the Overview panel, which
 * calls hand_over_project so the membership rows move with it.
 */
export type GrantableRole = Extract<ProjectRole, 'member' | 'viewer'>;

/** A membership row joined to the member's readable identity. */
export interface ProjectMemberWithProfile {
  id: string;
  user_id: string;
  project_role: ProjectRole;
  display_name: string;
  email: string;
  /** Null for accounts with no Google picture, and for unreadable profiles. */
  avatar_url: string | null;
}

export interface ProjectWorkspace {
  project: Project;
  members: ProjectMemberWithProfile[];
  sections: ProjectSection[];
  statuses: ProjectStatus[];
}

export interface CreateProjectInput {
  name: string;
  description: string;
  visibility: ProjectVisibility;
  templateKey: string;
}

export const VISIBILITY_LABELS: Record<ProjectVisibility, string> = {
  members_only: 'Members only',
  all_tap: 'All TAP',
};

export const PROJECT_ROLE_LABELS: Record<ProjectRole, string> = {
  owner: 'Owner',
  member: 'Member',
  viewer: 'Viewer',
};

/**
 * Whether the UI should offer a project-creation entry point.
 *
 * Convenience only: create_project_for_me() re-derives all of it from
 * auth.uid() and refuses TAP Viewers, suppliers, inactive and unapproved
 * callers — and, since Batch 10b, plain members while
 * workspace_settings.allow_member_project_creation is off. create_project()
 * itself is no longer executable from a browser, so hiding the button is not
 * what stops anybody.
 *
 * `allowMembers` is the workspace setting; undefined means it has not loaded
 * yet and is read as allowed, matching the column default. Reading an unknown
 * setting as "off" would flicker the New project button out of existence on
 * every page load.
 */
export function canCreateProject(profile: Profile, allowMembers?: boolean): boolean {
  if (!profile.is_active || profile.account_type !== 'tap') {
    return false;
  }
  if (profile.internal_role === 'tap_admin' || profile.is_owner) {
    // The setting reads "members may create projects". Somebody has to be
    // able to while it is switched off, or turning it off locks the workspace.
    return true;
  }
  if (profile.internal_role !== 'tap_member') {
    return false;
  }
  return allowMembers !== false;
}

/**
 * Whether the UI should offer task creation and editing in this project.
 *
 * Mirrors can_edit_project(): a TAP Admin anywhere, otherwise the project
 * owner or a member holding the owner or member project role. A global TAP
 * Viewer is read-only whatever their project role (contract 4.3). The
 * database re-derives all of this and refuses anything not permitted.
 */
export function canEditProject(
  profile: Profile,
  ownerId: string,
  members: ProjectMemberWithProfile[],
): boolean {
  if (!profile.is_active || profile.account_type !== 'tap') {
    return false;
  }
  if (profile.internal_role === 'tap_viewer') {
    return false;
  }
  if (profile.internal_role === 'tap_admin') {
    return true;
  }
  if (profile.id === ownerId) {
    return true;
  }
  return members.some(
    (member) =>
      member.user_id === profile.id &&
      (member.project_role === 'owner' || member.project_role === 'member'),
  );
}

/**
 * Whether the signed-in user is globally read-only. A global tap_viewer stays
 * read-only regardless of any project role (contract 4.3).
 */
export function isReadOnlyUser(profile: Profile): boolean {
  return profile.internal_role === 'tap_viewer';
}

/** Projects grouped by their free-form category, uncategorized last. */
export interface ProjectCategoryGroup {
  category: string;
  projects: ProjectListItem[];
}

export function groupProjectsByCategory(projects: ProjectListItem[]): ProjectCategoryGroup[] {
  // Case-insensitive: "sales" and "Sales" are one group, shown with the
  // first spelling encountered.
  const named = new Map<string, { label: string; projects: ProjectListItem[] }>();
  const loose: ProjectListItem[] = [];
  for (const project of projects) {
    const category = project.category.trim();
    if (category === '') {
      loose.push(project);
    } else {
      const key = category.toLocaleLowerCase();
      const existing = named.get(key);
      if (existing) {
        existing.projects.push(project);
      } else {
        named.set(key, { label: category, projects: [project] });
      }
    }
  }
  const groups = [...named.values()]
    .sort((left, right) => left.label.localeCompare(right.label))
    .map((group) => ({ category: group.label, projects: group.projects }));
  if (loose.length > 0) {
    groups.push({ category: '', projects: loose });
  }
  return groups;
}

/** Every distinct category in use, first spelling wins, alphabetical. */
export function knownCategories(projects: ProjectListItem[]): string[] {
  return groupProjectsByCategory(projects)
    .map((group) => group.category)
    .filter((category) => category !== '');
}

/**
 * What a typed category should be stored as: when it matches an existing
 * category apart from case or stray spaces, snap to the existing spelling so
 * one letter of drift cannot split a group.
 */
export function normalizeCategory(typed: string, existing: string[]): string {
  const clean = typed.trim();
  const match = existing.find(
    (category) => category.toLocaleLowerCase() === clean.toLocaleLowerCase(),
  );
  return match ?? clean;
}
